import cx from "classnames";

import Box from "client/common/components/layouts/Box";
import Banner from "client/common/components/content/Banner";
import { urls } from "client/routes";
import { ModalStatus } from "client/common/types";
import Button from "client/common/components/buttons/Button";
import PaymentMethod from "client/common/components/content/PaymentMethod";

import { ListSubscription as ClientListSubscription, SubscriptionForm } from "../types";
import SubscriptionItem from "./SubscriptionItem";
import SubscriptionActions from "./SubscriptionActions";
import { getSubscriptionUserActions } from "../utils";
import SubscriptionStatus from "./SubscriptionStatus";

import styles from "../styles/ListSubscription.module.less";

type SummaryBarProps = {
  subscription: ClientListSubscription;
};

const SummaryBar = ({ subscription }: SummaryBarProps) => {
  const summaryItems = [
    {
      value: subscription.patient.name_display,
      label: "Pet Name",
      className: styles.patient,
    },
    {
      value: subscription.payment_method ? (
        <PaymentMethod.Short className={styles.method} method={subscription.payment_method} />
      ) : (
        "No payment method configured"
      ),
      label: "Payment Method",
      className: styles.payment,
    },

    {
      value: subscription.shipping_address?.address_one || "No address configured",
      label: "Shipping Address",
      className: styles.address,
    },
    ...(subscription.shipping_address
      ? [
          {
            value: `${subscription.shipping_address?.city}, ${subscription.shipping_address?.state}`,
            label: "City / State",
            className: styles.city,
          },
        ]
      : []),
  ];

  return (
    <div className={styles.summaryBar}>
      {summaryItems.map((info) => (
        <div className={cx(styles.info, info.className)} key={info.label}>
          <div className={styles.value}>{info.value}</div>
          <div className={styles.label}>{info.label}</div>
        </div>
      ))}
    </div>
  );
};

type ListSubscriptionProps = {
  subscription: ClientListSubscription;
  index: number;
  total: number;
};

const ListSubscription = ({ subscription, index, total }: ListSubscriptionProps) => {
  return (
    <Box className={styles.listSubscription} key={subscription.id}>
      <div className={styles.header}>
        <SummaryBar subscription={subscription} />
        <h4 className={styles.title}>
          Autoship {index + 1} of {total}
        </h4>
        <div className={styles.status}>
          <SubscriptionStatus subscription={subscription} />
        </div>
      </div>
      {/* TODO: Hook up to card errors */}
      {false && (
        <Banner inline={true} variant="danger">
          <Banner.Text
            label="Your current payment method on file has been declined."
            action={{
              children: "Add or update payment method",
              dest: urls.subscriptions.detail({
                params: { id: subscription.id },
                search: {
                  form: SubscriptionForm.UpdatePayment,
                  visible: ModalStatus.Open,
                },
              }),
            }}
          />
        </Banner>
      )}
      <div className={styles.content}>
        <div className={styles.contentItem}>
          <SubscriptionItem subscription={subscription} key={subscription.id} />
        </div>
        <div className={styles.contentModify}>
          <SubscriptionActions subscription={subscription} />
        </div>
      </div>
      <div className={styles.actions}>
        <div className={styles.secondaryActions}>
          {getSubscriptionUserActions(subscription).map((action) => (
            <Button
              className={styles.userAction}
              key={action.action}
              dest={action.dest}
              size="s"
              variant="secondary"
            >
              {action.label}
            </Button>
          ))}
        </div>
        <div>
          <Button
            className={styles.detailAction}
            size="s"
            dest={urls.subscriptions.detail({ params: { id: subscription.id } })}
          >
            View Autoship Details
          </Button>
        </div>
      </div>
    </Box>
  );
};

export default ListSubscription;
